import { useState } from 'react'

import { format, isWeekend } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import './MyTeamTab.scss'
import { MyTeamSummaryCards } from './MyTeamSummaryCards'
import { MyTeamTableRow } from './MyTeamTableRow'
import { MyTeamTooltip } from './MyTeamTooltip'
import { useMyTeamData } from './useMyTeamData'
import { calculateSummaryMetrics, useMyTeamSummary } from './useMyTeamSummary'
import { TimeTrackingControlBar } from '../../components/TimeTrackingControlBar'
import { MY_TEAM_CONSTANTS, TimeTrackingTab } from '../../const'
import { useTeamLeaders } from '../../hooks/useTeamLeaders'
import { FilterValues, MyTeamTabProps } from '../../types'
import type { DisplayFormat } from '../../types'
import { exportMyTeamToCSV } from '../../utils/csvExport'
import {
  getMonthRange,
  getDaysInMonth,
  formatDateWithLocale,
} from '../../utils/dateUtils'
import { Badge } from '@/Atomic/Atoms/Badge'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'

export const MyTeamTab = connect(
  ({
    openLogTimeDrawer,
    navigateToTab,
    navigateToResource,
    initialFilters,
    monthRange,
    onMonthRangeChange,
    onFilterChange,
    currentUserId,
  }: MyTeamTabProps) => {
    const { t } = useTranslation()
    const filters: FilterValues = initialFilters || {
      status: null,
      team: null,
      customer: null,
      order: null,
      workPackage: null,
    }
    const [displayFormat, setDisplayFormat] = useState<DisplayFormat>('hours')

    const teamLeaders = useTeamLeaders()
    const { employees, loadMore, totalEmployees, totalRecords, hasMore } =
      useMyTeamData(monthRange, currentUserId, filters)
    const daysInMonth = getDaysInMonth(monthRange)
    const { summaryMetrics, dayData, allRecordIds } = useMyTeamSummary(
      employees,
      daysInMonth,
    )

    const hasRecords = allRecordIds.length > 0

    const [isExporting, setIsExporting] = useState(false)

    const handleExportCSV = async () => {
      setIsExporting(true)
      try {
        const { loadAllEmployees } = await import('./exportAllEmployees')
        const allEmployees = await loadAllEmployees(
          filters?.team || currentUserId,
          monthRange,
        )
        const monthLabel = format(new Date(monthRange.startDate), 'MMMM-yyyy')
        exportMyTeamToCSV(
          allEmployees,
          daysInMonth,
          calculateSummaryMetrics(allEmployees),
          monthLabel,
        )
      } finally {
        setIsExporting(false)
      }
    }

    const handleNavigateAllTimes = hasRecords
      ? () =>
          navigateToTab(TimeTrackingTab.ALL_TIMES, {
            dateRange: monthRange,
          })
      : undefined

    const metricHeaderClass = `my-team-metric-header${hasRecords ? ' my-team-clickable' : ''}`

    return (
      <div>
        <TimeTrackingControlBar
          weekRange={monthRange}
          statusOptions={[]}
          teamOptions={teamLeaders}
          displayFormat={displayFormat}
          onDisplayFormatChange={setDisplayFormat}
          onWeekChange={onMonthRangeChange}
          onNavigateWeek={(direction) => {
            const currentDate = new Date(monthRange.startDate)
            const newDate = new Date(currentDate)
            newDate.setMonth(
              currentDate.getMonth() + (direction === 'next' ? 1 : -1),
            )
            onMonthRangeChange(getMonthRange(newDate))
          }}
          onCurrentWeek={() => onMonthRangeChange(getMonthRange(new Date()))}
          onFilterChange={onFilterChange}
          onLogTime={() => openLogTimeDrawer()}
          onExportCSV={handleExportCSV}
          isExporting={isExporting}
          currentTab={TimeTrackingTab.MY_TEAM}
        />

        <div className="my-team-scope-bar">
          <span className="my-team-scope-bar-text">
            <strong>{employees.length}</strong>{' '}
            {t('timeTracking.billing.ofEmployees', { total: totalEmployees })}
            {' · '}
            <strong>{totalRecords}</strong> {t('timeTracking.billing.records')}
            {hasMore && (
              <span className="my-team-scope-bar-note">
                {' · '}
                {t('timeTracking.billing.statsReflect')}
              </span>
            )}
            {hasMore && (
              <button
                className="my-team-scope-bar-load-more"
                onClick={loadMore}
                type="button"
              >
                {t('timeTracking.loadMore')}
              </button>
            )}
          </span>
        </div>

        <MyTeamSummaryCards
          metrics={summaryMetrics}
          displayFormat={displayFormat}
          employeeCount={employees.length}
          dateLabel={
            formatDateWithLocale(new Date(monthRange.startDate), 'MMM d') +
            '-' +
            format(new Date(monthRange.endDate), 'd')
          }
        />

        <div className="time-tracking-table-container">
          <table className="time-tracking-table my-team-table">
            <thead>
              <tr>
                <th className="my-team-member-column">
                  {t('timeTracking.billing.teamMember')}
                </th>
                <th
                  className={metricHeaderClass}
                  onClick={handleNavigateAllTimes}
                >
                  {t('timeTracking.billing.tracked')}
                </th>
                <th
                  className={metricHeaderClass}
                  onClick={handleNavigateAllTimes}
                >
                  {t('timeTracking.billing.billable')}
                </th>
                <th
                  className={metricHeaderClass}
                  title={t('timeTracking.billing.nonBillable')}
                  onClick={handleNavigateAllTimes}
                >
                  {t('timeTracking.billing.nonBillableShort')}
                </th>
                <th className="my-team-metric-header">
                  {t('timeTracking.billing.support')}
                </th>
                <th
                  className="my-team-metric-header"
                  title={t('timeTracking.billing.managedTeam')}
                >
                  {t('timeTracking.billing.managedTeamShort')}
                </th>
                <th
                  className="my-team-metric-header"
                  title={t('timeTracking.billing.notTracked')}
                >
                  {t('timeTracking.billing.notTrackedShort')}
                </th>
                <th className="my-team-utilization-header">
                  {t('timeTracking.billing.utilShort')}
                </th>
                {daysInMonth.map((day) => {
                  const dateKey = format(day, 'yyyy-MM-dd')
                  const dayInfo = dayData.get(dateKey)

                  return (
                    <th
                      key={dateKey}
                      className={`my-team-day-header${isWeekend(day) ? ' my-team-day-header--weekend' : ''}`}
                      {...(dayInfo && dayInfo.total > 0
                        ? {
                            'data-tooltip-id': 'my-team-header-tooltip',
                            'data-tooltip-billable': String(dayInfo.billable),
                            'data-tooltip-non-billable': String(
                              dayInfo.nonBillable,
                            ),
                            'data-tooltip-utilization': String(
                              dayInfo.utilization,
                            ),
                          }
                        : {})}
                    >
                      <div>{day.getDate()}</div>
                      {dayInfo && dayInfo.hasMissing && (
                        <div className="my-team-day-badges">
                          <Badge variant="danger" value="M" />
                        </div>
                      )}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      daysInMonth.length +
                      MY_TEAM_CONSTANTS.METRIC_COLUMNS_COUNT
                    }
                    className="time-tracking-empty-state"
                  >
                    <div>
                      <div className="time-tracking-empty-text">
                        {t('timeTracking.billing.noData')}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <MyTeamTableRow
                    key={employee.userId}
                    employee={employee}
                    daysInMonth={daysInMonth}
                    navigateToTab={navigateToTab}
                    navigateToResource={navigateToResource}
                    displayFormat={displayFormat}
                    monthRange={monthRange}
                    currentLeaderId={filters.team || currentUserId}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        <Tooltip
          id="my-team-header-tooltip"
          place="bottom"
          noArrow
          className="my-team-tooltip-container"
          delayShow={0}
          delayHide={0}
          render={({ activeAnchor }) => {
            const billable = Number(
              activeAnchor?.getAttribute('data-tooltip-billable') || 0,
            )
            const nonBillable = Number(
              activeAnchor?.getAttribute('data-tooltip-non-billable') || 0,
            )
            const utilization = Number(
              activeAnchor?.getAttribute('data-tooltip-utilization') || 0,
            )
            return (
              <MyTeamTooltip
                billableHours={billable}
                nonBillableHours={nonBillable}
                utilization={utilization}
                showUtilization
              />
            )
          }}
        />

        <Tooltip
          id="my-team-cell-tooltip"
          place="bottom"
          noArrow
          className="my-team-tooltip-container"
          delayShow={0}
          delayHide={0}
          render={({ activeAnchor }) => {
            const billable = Number(
              activeAnchor?.getAttribute('data-tooltip-billable') || 0,
            )
            const nonBillable = Number(
              activeAnchor?.getAttribute('data-tooltip-non-billable') || 0,
            )
            return (
              <MyTeamTooltip
                billableHours={billable}
                nonBillableHours={nonBillable}
                utilization={0}
              />
            )
          }}
        />
      </div>
    )
  },
)
